// Listrak notify button management
(() => {
  'use strict';

  function insertNotifyButton(productOverview, variant) {
    if (!productOverview || !productOverview.elements?.productActions) return;

    if(!productOverview.dataset.enableListrakBis === 'true') return;

    // Check if button already exists
    if (productOverview.elements.productActions.querySelector('.listrak-notify-button')) return;

    const isAvailable = variant ? variant.available_shopify : false;
    if (isAvailable) return; // Don't insert if variant is available

    // Create the notify button element
    const notifyButton = document.createElement('modal-opener');
    notifyButton.className = 'listrak-notify-button no-js-hidden';
    notifyButton.dataset.modal = '#back-in-stock';

    notifyButton.innerHTML = `
      <button type="button" class="button button--outline js-notify-button">
        <span class="button__text">
          Notify me when available
          ${!productOverview.product?.has_only_default_variant ? `<span class="atc-variant-info desktop-hidden">${variant?.title ? variant.title.replace(' / ', ' - ') : ''}</span>` : ''}
        </span>
      </button>
    `;

    productOverview.elements.productActions.appendChild(notifyButton);
  }

  function updateNotifyButton(productOverview, variant) {
    if (!productOverview || !productOverview.elements?.productActions) return;

    let notifyButton = productOverview.elements.productActions.querySelector('.listrak-notify-button');
    const enableListrakBIS = productOverview.dataset.enableListrakBis === 'true';

    if (!enableListrakBIS) {
      // Remove notify button if Listrak is disabled
      if (notifyButton) {
        notifyButton.remove();
      }
      return;
    }

    const isAvailable = variant ? variant.available_shopify : false;

    if (isAvailable) {
      // Hide notify button when variant is available
      if (notifyButton) {
        notifyButton.style.display = 'none';
      }
    } else {
      // Show notify button when variant is unavailable
      if (notifyButton) {
        notifyButton.style.display = '';
        updateNotifyButtonVariantInfo(notifyButton, variant, productOverview.product);
      } else {
        // Insert button if it doesn't exist
        insertNotifyButton(productOverview, variant);
      }
    }
  }

  function updateNotifyButtonVariantInfo(notifyButton, variant, product) {
    if (!notifyButton || !variant) return;

    const variantInfoSpan = notifyButton.querySelector('.atc-variant-info');
    if (variantInfoSpan && product?.has_only_default_variant === false) {
      variantInfoSpan.textContent = variant.title ? variant.title.replace(' / ', ' - ') : '';
    }
  }

  // Listen for product overview variant changes
  document.addEventListener('product-overview:variant-change', (event) => {
    const { productOverview, variant } = event.detail;
    updateNotifyButton(productOverview, variant);
  });

  // Initialize notify buttons for existing product overviews
  document.querySelectorAll('product-overview').forEach(productOverview => {
    if (productOverview.variant) {
      updateNotifyButton(productOverview, productOverview.variant);
    }
  });

})();

// ListrakNotifyButton custom element removed - notify button now rendered directly in template

if(!customElements.get('listrak-bis-form')) {
  class ListrakBISForm extends HTMLElement {
    constructor() {
      super();
      this.BISForm = null;
      this.eventListeners = [];
      this.isSubmitting = false;
      this.phoneMask = null;
      this.phoneInput = null;
      this.hiddenPhoneInput = null;
    }

    /**
     * Validates and extracts form data
     * @returns {Object|null} Form data object or null if invalid
     */
    extractFormData() {
      if (!this.BISForm) return null;

      const formData = new FormData(this.BISForm);
      const email = formData.get("email")?.trim() || '';

      // Get phone value from hidden input if it exists (for IMask), otherwise from visible input
      let phone = '';
      if (this.hiddenPhoneInput) {
        phone = this.hiddenPhoneInput.value?.trim() || '';
      } else {
        phone = formData.get("phone")?.trim() || '';
      }

      const sku = formData.get("variant_sku")?.trim() || '';
      const alertCode = formData.get("alert_code")?.trim() || '';

      // Basic validation
      if (!email && !phone) {
        this.showError('Please provide either an email address or phone number.');
        return null;
      }

      if (email && !this.isValidEmail(email)) {
        this.showError('Please enter a valid email address.');
        return null;
      }

      if (phone && !this.isValidPhone(phone)) {
        this.showError('Please enter a valid 10-digit US phone number (area code cannot start with 0 or 1).');
        return null;
      }

      return { email, phone, sku, alertCode };
    }

    /**
     * Basic email validation
     */
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    }

    /**
     * US phone validation - requires exactly 10 digits for US format
     */
    isValidPhone(phone) {
      // Remove all non-digit characters
      const cleanPhone = phone.replace(/\D/g, '');

      // US phone numbers must be exactly 10 digits
      if (cleanPhone.length !== 10) {
        return false;
      }

      // Basic format check - area code cannot start with 0 or 1
      const areaCodeFirstDigit = cleanPhone.charAt(0);
      if (areaCodeFirstDigit === '0' || areaCodeFirstDigit === '1') {
        return false;
      }

      return true;
    }

    /**
     * Check if phone number is complete (has all required digits)
     */
    isPhoneComplete(phone) {
      const cleanPhone = phone.replace(/\D/g, '');
      return cleanPhone.length === 10;
    }

    /**
     * Shows error message to user
     */
    showError(message) {
      // Remove existing error messages
      const existingError = this.BISForm.querySelector('.form-error-message');
      if (existingError) {
        existingError.remove();
      }

      // Create and insert error message
      const errorElement = document.createElement('div');
      errorElement.className = 'form-error-message';
      errorElement.setAttribute('role', 'alert');
      errorElement.setAttribute('aria-live', 'polite');
      errorElement.textContent = message;

      // Insert after form or at top of form
      const formFirstChild = this.BISForm.firstElementChild;
      if (formFirstChild) {
        this.BISForm.insertBefore(errorElement, formFirstChild);
      } else {
        this.BISForm.appendChild(errorElement);
      }

      // Focus the first invalid field
      const firstInvalidField = this.BISForm.querySelector(':invalid');
      if (firstInvalidField) {
        firstInvalidField.focus();
      }
    }

    /**
     * Shows success message
     */
    showSuccess() {
      const backInStock = this.BISForm.closest('.js-back-in-stock');
      if (backInStock) {
        backInStock.classList.add('product__stock--success');
        // Announce success to screen readers
        const successMessage = backInStock.querySelector('.success-message');
        if (successMessage) {
          successMessage.setAttribute('aria-live', 'polite');
        }
      }
    }

    /**
     * Shows incomplete phone number message
     */
    showPhoneIncompleteMessage(phoneInput) {
      // Remove existing incomplete message
      this.hidePhoneIncompleteMessage();

      // Create incomplete message
      const incompleteMessage = document.createElement('div');
      incompleteMessage.className = 'phone-incomplete-message';
      incompleteMessage.setAttribute('role', 'status');
      incompleteMessage.setAttribute('aria-live', 'polite');
      incompleteMessage.textContent = 'Please complete the phone number (10 digits required)';

      // Insert after the phone input
      const phoneField = phoneInput.closest('.field-phone');
      if (phoneField) {
        phoneField.appendChild(incompleteMessage);
      }
    }

    /**
     * Hides incomplete phone number message
     */
    hidePhoneIncompleteMessage() {
      const existingMessage = this.BISForm.querySelector('.phone-incomplete-message');
      if (existingMessage) {
        existingMessage.remove();
      }
    }

    /**
     * Handles form submission with improved error handling
     */
    async handleStockFormSubmit() {
      if (this.isSubmitting) return;

      // Prevent double submission
      this.isSubmitting = true;
      this.setSubmitButtonState(true);

      try {
        const formData = this.extractFormData();
        if (!formData) {
          this.isSubmitting = false;
          this.setSubmitButtonState(false);
          return;
        }

        await this.submitToListrak(formData);
        this.showSuccess();

      } catch (error) {
        console.error('BIS form submission error:', error);
        this.showError('An error occurred. Please try again.');
      } finally {
        this.isSubmitting = false;
        this.setSubmitButtonState(false);
      }
    }

    /**
     * Sets submit button loading state
     */
    setSubmitButtonState(isLoading) {
      const submitButton = this.BISForm.querySelector('button[type="submit"]');
      if (!submitButton) return;

      submitButton.disabled = isLoading;
      submitButton.setAttribute('aria-busy', isLoading.toString());

      if (isLoading) {
        submitButton.dataset.originalText = submitButton.textContent;
        submitButton.textContent = 'Submitting...';
      } else {
        submitButton.textContent = submitButton.dataset.originalText || 'Notify Me';
      }
    }

    /**
     * Submits data to Listrak with proper error handling
     */
    submitToListrak(formData) {
      return new Promise((resolve, reject) => {
        // Listrak recommended format for async _ltk object listener
        // See: https://help.listrak.com/en/articles/4820523-javascript-website-integration-installation
        const submitData = () => {
          try {
            if (!window._ltk || !window._ltk.Alerts) {
              throw new Error('Listrak library not available');
            }

            // Listrak format for sending Alerts data
            const alertsData = {
              Identifiers: {
                Email: formData.email,
                PhoneNumber: formData.phone
              },
              Sku: formData.sku,
              AlertCode: formData.alertCode
            };

            // Listrak Alerts send method
            window._ltk.Alerts.AddAlertWithIdentifiers(alertsData);
            window._ltk.Alerts.Submit();

            resolve();
          } catch (error) {
            reject(error);
          }
        };

        // Handle async Listrak loading
        if (typeof window._ltk === "undefined") {
          if (document.addEventListener) {
            const handleLtkReady = () => {
              document.removeEventListener("ltkAsyncListener", handleLtkReady);
              submitData();
            };
            document.addEventListener("ltkAsyncListener", handleLtkReady);
          } else {
            // Fallback for older browsers
            const element = document.documentElement;
            element.ltkAsyncProperty = 0;
            element.attachEvent("onpropertychange", function(e) {
              if (e.propertyName === "ltkAsyncProperty") {
                element.detachEvent("onpropertychange", arguments.callee);
                submitData();
              }
            });
          }
        } else {
          submitData();
        }
      });
    }

    /**
     * Updates form validation state with better UX
     */
    updateFormValidity() {
      if (!this.BISForm) return;

      const submitButton = this.BISForm.querySelector('button[type="submit"]');
      if (!submitButton) return;

      const isValid = this.BISForm.checkValidity();
      submitButton.disabled = !isValid || this.isSubmitting;

      // Update aria-describedby for better screen reader support
      const errorMessage = this.BISForm.querySelector('.form-error-message');
      if (errorMessage && errorMessage.id) {
        submitButton.setAttribute('aria-describedby', errorMessage.id);
      } else {
        submitButton.removeAttribute('aria-describedby');
      }
    }

    /**
     * Updates variant references in form
     */
    updateVariantReference(event) {
      if (!event?.detail?.variant || !this.BISForm) return;

      const currentVariant = event.detail.variant;

      // Update individual input fields with correct variant properties
      const variantInput = this.BISForm.querySelector('input[name="variant"]');
      const variantSkuInput = this.BISForm.querySelector('input[name="variant_sku"]');
      const productInput = this.BISForm.querySelector('input[name="product"]');

      if (variantInput) variantInput.value = currentVariant.id ?? '';
      if (variantSkuInput) variantSkuInput.value = currentVariant.sku ?? '';
      if (productInput) productInput.value = currentVariant.product_id ?? '';
    }

    /**
     * Sets up phone input logic for marketing checkbox requirement and validation
     */
    setupPhoneMarketingLogic() {
      // Find phone input - could be masked with IMask or regular input
      const phoneField = this.BISForm.querySelector('.field-phone');
      if (!phoneField) return;

      // Try to find the actual input (could be masked or hidden)
      let phoneInput = phoneField.querySelector('input:not([type="hidden"])');
      if (!phoneInput) {
        phoneInput = phoneField.querySelector('input');
      }

      const hiddenPhoneInput = phoneField.querySelector('input[type="hidden"]');
      const checkboxInput = this.BISForm.querySelector('#accept-marketing');

      if (!phoneInput) return;

      // Store references for later use
      this.phoneInput = phoneInput;
      this.hiddenPhoneInput = hiddenPhoneInput;

      // Setup IMask if available (loaded globally via myntr-bundle) and not already masked
      if (typeof window.IMask !== 'undefined' && !phoneInput.hasAttribute('data-imask-applied')) {
        this.phoneMask = window.IMask(phoneInput, {
          mask: '(000) 000-0000'
        });
        phoneInput.setAttribute('data-imask-applied', 'true');
      }

      const updatePhoneValidation = () => {
        // Get the actual phone value (from hidden input if it exists, otherwise from visible input)
        const phoneValue = hiddenPhoneInput ? hiddenPhoneInput.value : phoneInput.value;
        const isValid = !phoneValue || this.isValidPhone(phoneValue);
        const isComplete = this.isPhoneComplete(phoneValue);

        // Update input validity
        phoneInput.setCustomValidity(isValid ? '' : 'Please enter a valid 10-digit US phone number.');

        // Show/hide validation message for incomplete numbers
        if (phoneValue && !isComplete) {
          this.showPhoneIncompleteMessage(phoneInput);
        } else {
          this.hidePhoneIncompleteMessage();
        }

        // Update checkbox requirement
        if (checkboxInput) {
          const hasPhoneValue = phoneValue.trim().length > 0;
          checkboxInput.required = hasPhoneValue;
          checkboxInput.setAttribute('aria-required', hasPhoneValue.toString());
        }

        // Update form validity
        this.updateFormValidity();
      };

      // Add event listeners for real-time validation
      const inputHandler = () => updatePhoneValidation();

      phoneInput.addEventListener('input', inputHandler);
      phoneInput.addEventListener('blur', inputHandler);

      // Listen to hidden input changes if it exists (for IMask)
      if (hiddenPhoneInput) {
        hiddenPhoneInput.addEventListener('input', inputHandler);
      }

      this.eventListeners.push(
        { element: phoneInput, type: 'input', handler: inputHandler },
        { element: phoneInput, type: 'blur', handler: inputHandler }
      );

      if (hiddenPhoneInput) {
        this.eventListeners.push({
          element: hiddenPhoneInput,
          type: 'input',
          handler: inputHandler
        });
      }

      // Set initial state
      updatePhoneValidation();
    }

    /**
     * Initializes all event listeners
     */
    initEventListeners() {
      if (!this.BISForm) return;

      // Form submission
      const handleSubmit = (event) => {
        event.preventDefault();
        this.handleStockFormSubmit();
      };

      // Form validation
      const handleChange = () => {
        this.updateFormValidity();
      };

      // Variant changes
      const handleVariantChange = (event) => {
        this.updateVariantReference(event);
      };

      // Add listeners and store references
      this.eventListeners.push(
        { element: this.BISForm, type: 'submit', handler: handleSubmit },
        { element: this.BISForm, type: 'change', handler: handleChange },
        { element: document, type: 'product-overview:variant-change', handler: handleVariantChange }
      );

      // Attach listeners
      this.eventListeners.forEach(({ element, type, handler }) => {
        element.addEventListener(type, handler);
      });
    }

    /**
     * Cleanup method to remove event listeners and IMask
     */
    cleanup() {
      this.eventListeners.forEach(({ element, type, handler }) => {
        element.removeEventListener(type, handler);
      });
      this.eventListeners = [];

      // Destroy IMask instance if it exists
      if (this.phoneMask) {
        this.phoneMask.destroy();
        this.phoneMask = null;
      }

      // Hide any incomplete phone messages
      this.hidePhoneIncompleteMessage();
    }

    connectedCallback() {
      this.BISForm = this.querySelector('#stock-form');
      if (!this.BISForm) return;

      // Setup phone/marketing logic
      this.setupPhoneMarketingLogic();

      // Initialize event listeners
      this.initEventListeners();

      // Set initial form state
      this.updateFormValidity();
    }

    disconnectedCallback() {
      this.cleanup();
    }
  }

  customElements.define('listrak-bis-form', ListrakBISForm);
}

(() => {
  'use strict';
  const listrakFormContainer = document.querySelector(".newsletter-form-l");

  if (!listrakFormContainer) {
    return;
  }

  function templateSwap() {
    const listrakFormTemplate = listrakFormContainer.querySelector(
      "#listrak-footer-form-template"
    );
    const listrakForm =
      listrakFormTemplate.content.firstElementChild.cloneNode(true);
    const formContainer = listrakFormTemplate.parentNode;
    formContainer.innerHTML = listrakForm.innerHTML;
  }

  function handleFormSubmission(formElement) {
    const actionURL = formElement.action;
    const method = formElement.method || "POST";

    formElement.removeAttribute("action");
    formElement.removeAttribute("method");

    formElement.addEventListener("submit", (event) => {
      event.preventDefault();

      const formData = new FormData(formElement);

      // due to CORS rules, we have to set mode to "no-cors" this will let us know if our request made it, but we won't know the results of post.
      // we will just have to happily take any submission and set a "thank you" message.
      fetch(actionURL, {
        method: method,
        body: formData,
        mode: "no-cors", // Add this line
        headers: { Accept: "application/json" },
      })
        .then(() => {
          console.log("Request was sent successfully");
          const formInner = formElement.querySelector(
            ".newsletter-form-l-inner"
          );
          if (formInner) {
            formInner.innerHTML = "<span>Thank You For Subscribing</span>";
          }
        })
        .catch((error) => {
          console.error("There was a problem with the fetch operation:", error);
        });
    });
  }

  const listrakFormContainerCallback = (mutationsList, observer) => {
    for (const mutation of mutationsList) {
      if (mutation.type === "childList") {
        const formElement = listrakFormContainer.querySelector("form");
        if (formElement) {
          handleFormSubmission(formElement);
          observer.disconnect();
          return;
        }
      }
    }
  };

  const listrakFormContainerObserver = new MutationObserver(
    listrakFormContainerCallback
  );
  listrakFormContainerObserver.observe(listrakFormContainer, {
    childList: true,
    subtree: true,
  });
  templateSwap();
})();

// PDP back in stock modal insertion
(()=>{
  'use strict';

  function insertBISModal(container){
    if(!container) return;
    if(container.dataset.enableListrakBis != "true") return;
    const bisModalTemplate = document.querySelector('#listrak-bis-modal-template');
    if(!bisModalTemplate) return;
    const existingBisModal = document.querySelector('#back-in-stock');
    if (existingBisModal) {
      existingBisModal.remove();
    }
    const bisModal = bisModalTemplate.content.cloneNode(true);
    let bisModalContainerSelector = '#listrak-bis-modal-container';
    const uniqueId = container.querySelector('.js-unique-id');
    if(uniqueId){
      bisModalContainerSelector += `--${uniqueId.value}`;
    }
    const bisModalContainer = container.querySelector(`${bisModalContainerSelector}`);
    if(bisModalContainer){
      bisModalContainer.appendChild(bisModal);
    }
  }

  // Insert BIS modal for main product overview
  const productOverview = document.querySelector('.product-overview');
  if(productOverview){
    insertBISModal(productOverview);
  }

  // Insert BIS modal for lookbook products
  const lookbookProducts = document.querySelectorAll('lookbook-product');
  if(lookbookProducts){
    lookbookProducts.forEach((lookbookProduct)=>{
      insertBISModal(lookbookProduct);
    });
  }

  // Observer for main content - handles dynamic product overview loading (e.g., quickview on PLP)
  const mainContent = document.querySelector('#main-content');
  if (mainContent) {
    const config = { childList: true, subtree: true };

    const callback = function(mutationsList, observer) {
      for(const mutation of mutationsList) {
        if (mutation.type === 'childList') {
          for(const addedNode of mutation.addedNodes) {
            if (addedNode.classList && addedNode.classList.contains('shopify-section--product-overview')) {
              const productOverview = addedNode.querySelector('.product-overview');
              if(productOverview){
                insertBISModal(productOverview);
              }
            }
          }
        }
      }
    };

    const observer = new MutationObserver(callback);
    observer.observe(mainContent, config);
  }

})();


(()=>{
  'use strict';
  var biJsHost = (("https:" == document.location.protocol) ? "https://" : "http://");
  (function (d, s, id, tid, vid) {
      var js, ljs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) return; js = d.createElement(s); js.id = id;
      js.src = biJsHost + "cdn.listrakbi.com/scripts/script.js?m=" + tid + "&v=" + vid;
      js.async = true;
      ljs.parentNode.insertBefore(js, ljs);
  })(document, 'script', 'ltkSDK', 'qZvAVUdupT1S', '1');

  if(window.theme.customer.id) {
    window.ltkCustObj = { "firstname": window.theme?.customer?.first_name, "lastname": window.theme?.customer?.last_name, "email": window.theme?.customer?.email};
  }
})();
